import React, { useState } from 'react'
import {
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Box,
    InputAdornment,
    useTheme,
    CircularProgress,
    IconButton
} from '@mui/material'
import {
    Lock as LockIcon,
    CheckCircle as CheckCircleIcon,
    ArrowBack as ArrowBackIcon,
    Visibility,
    VisibilityOff
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { authApi } from '../../api/authApi.js'
import toast from 'react-hot-toast'
import { useLoading } from '../../hooks'

const ChangePassword = () => {
    const navigate = useNavigate()
    const { user, orgId } = useSelector((state) => state.auth)
    const { loading, withLoading } = useLoading()
    const theme = useTheme()
    const [success, setSuccess] = useState(false)
    const [showCurrentPassword, setShowCurrentPassword] = useState(false)
    const [showNewPassword, setShowNewPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)

    // Validation Schema
    const schema = yup.object({
        currentPassword: yup.string().required('Current password is required'),
        newPassword: yup
            .string()
            .min(6, 'Password must be at least 6 characters')
            .required('New password is required'),
        confirmPassword: yup
            .string()
            .oneOf([yup.ref('newPassword'), null], 'Passwords must match')
            .required('Confirm password is required'),
    })

    // Form handling
    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(schema)
    })

    const onSubmit = async (data) => {
        withLoading(async () => {
            try {
                const response = await authApi.employeeChangePassword(
                    {
                        current_password: data.currentPassword,
                        new_password: data.newPassword
                    },
                    {
                        'X-Employee-ID': user?.id,
                        'X-Org-ID': orgId
                    }
                )

                setSuccess(true)
            } catch (err) {
                console.error('Password change error:', err)
                toast.error(err.response?.data?.message || 'Failed to change password')
            }
        })
    }

    const renderPasswordInput = (name, label, showState, setShowState) => (
        <TextField
            margin="normal"
            fullWidth
            label={label}
            type={showState ? 'text' : 'password'}
            {...register(name)}
            error={!!errors[name]}
            helperText={errors[name]?.message}
            disabled={loading}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <LockIcon color="action" fontSize="small" />
                    </InputAdornment>
                ),
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowState(!showState)}
                            edge="end"
                            size="small"
                        >
                            {showState ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    </InputAdornment>
                )
            }}
            sx={{ mb: 2 }}
        />
    )

    return (
        <Box
            sx={{
                minHeight: '100vh',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: theme.palette.mode === 'dark'
                    ? 'radial-gradient(circle at 50% 50%, #1e293b 0%, #0f172a 100%)'
                    : 'linear-gradient(135deg, #f0f9ff 0%, #cbebff 100%)',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Abstract Background Shapes */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '-15%',
                    right: '-5%',
                    width: '500px',
                    height: '500px',
                    borderRadius: '50%',
                    background: theme.palette.mode === 'dark'
                        ? 'radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, rgba(0,0,0,0) 70%)'
                        : 'radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, rgba(255,255,255,0) 70%)',
                    zIndex: 0,
                }}
            />

            <Container component="main" maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
                <Paper
                    elevation={theme.palette.mode === 'dark' ? 4 : 0}
                    className="fade-in"
                    sx={{
                        p: { xs: 3, sm: 5 },
                        width: '100%',
                        borderRadius: 4,
                        background: theme.palette.mode === 'dark'
                            ? 'rgba(30, 41, 59, 0.7)'
                            : 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(20px)',
                        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.5)'}`,
                        boxShadow: theme.palette.mode === 'light'
                            ? '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                            : 'none',
                    }}
                >
                    {success ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Box
                                sx={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: '50%',
                                    background: `linear-gradient(135deg, ${theme.palette.success.light} 0%, ${theme.palette.success.main} 100%)`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mx: 'auto',
                                    mb: 3,
                                    boxShadow: '0 4px 12px rgba(46, 125, 50, 0.2)',
                                }}
                            >
                                <CheckCircleIcon sx={{ fontSize: 48, color: '#fff' }} />
                            </Box>
                            <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Password Updated!
                            </Typography>
                            <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
                                Your password has been changed successfully.
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={() => navigate('/employee/dashboard')}
                                sx={{ px: 4, py: 1.5, borderRadius: 2 }}
                            >
                                Go to Dashboard
                            </Button>
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ mb: 4, textAlign: 'center' }}>
                                <Box
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: 2,
                                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 2,
                                        mx: 'auto',
                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                    }}
                                >
                                    <LockIcon sx={{ color: '#fff', fontSize: 32 }} />
                                </Box>
                                <Typography component="h1" variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Change Password
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Create a new password for your account
                                </Typography>
                            </Box>

                            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                                {renderPasswordInput('currentPassword', 'Current Password', showCurrentPassword, setShowCurrentPassword)}
                                {renderPasswordInput('newPassword', 'New Password', showNewPassword, setShowNewPassword)}
                                {renderPasswordInput('confirmPassword', 'Confirm New Password', showConfirmPassword, setShowConfirmPassword)}

                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    disabled={loading}
                                    sx={{
                                        py: 1.5,
                                        mb: 2,
                                        mt: 2,
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                    }}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Update Password'}
                                </Button>

                                <Button
                                    fullWidth
                                    startIcon={<ArrowBackIcon />}
                                    onClick={() => navigate(-1)}
                                    disabled={loading}
                                    sx={{ borderRadius: 2 }}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </>
                    )}
                </Paper>
            </Container>
        </Box>
    )
}

export default ChangePassword
